/**
 * Document Detail Modal - Shows document info, importance editing, and download history.
 * Mirrors the legacy divDocumentsDetails modal with a two-column layout:
 *   Left: file details + importance radio buttons + category
 *   Right: download/activity log timeline
 */

import { useEffect, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  X, Download, FileText, Calendar, User, Clock, Loader2, ShieldCheck, CheckCircle,
  Eye, PlusCircle, Edit3, Bell,
} from 'lucide-react';
import { Checkbox } from '../../components/ui/checkbox';
import {
  documentsAPI,
  type Document as DocType,
  type DocumentCategory,
} from '../../lib/api';
import type { DocumentHistoryEntry } from '../../lib/api/documents';
import { useFormatters } from '../../contexts/PreferencesContext';
import { toast } from 'sonner';
import { CompanyConfirmationPanel } from './CompanyConfirmationPanel';

interface DocumentDetailModalProps {
  document: DocType;
  categories: DocumentCategory[];
  onClose: () => void;
  onUpdated: (doc: DocType) => void;
}

const IMPORTANCE_OPTIONS = [
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
] as const;

const IMPORTANCE_COLORS: Record<string, string> = {
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-blue-100 text-blue-700',
  low: 'bg-gray-100 text-gray-700',
  critical: 'bg-red-100 text-red-700',
};

// CR-5858: visual styling for each event_type surfaced by /history/.
// Mirrors the EVENT_TYPE_CONFIG in Changelog.tsx so the supplier-detail
// timeline reads the same way as the document-manager audit tab.
type EventVisual = { Icon: typeof Download; borderClass: string; iconClass: string };

function getEventVisual(entry: DocumentHistoryEntry): EventVisual {
  const t = entry.event_type || 'field_change';
  if (t === 'document_company_confirmed' || t === 'document_accepted') {
    return { Icon: CheckCircle, borderClass: 'border-green-600', iconClass: 'text-green-600' };
  }
  if (t === 'document_acceptance_enabled') {
    return { Icon: PlusCircle, borderClass: 'border-amber-500', iconClass: 'text-amber-600' };
  }
  if (t === 'document_notification_sent') {
    return { Icon: Bell, borderClass: 'border-blue-500', iconClass: 'text-blue-600' };
  }
  if (t === 'created') {
    return { Icon: PlusCircle, borderClass: 'border-slate-500', iconClass: 'text-slate-600' };
  }
  // Default field-change events: differentiate Downloaded vs Viewed by label.
  if ((entry.field_changed || '').toLowerCase() === 'downloaded') {
    return { Icon: Download, borderClass: 'border-primary', iconClass: 'text-primary' };
  }
  if ((entry.field_changed || '').toLowerCase() === 'viewed') {
    return { Icon: Eye, borderClass: 'border-primary', iconClass: 'text-primary' };
  }
  return { Icon: Edit3, borderClass: 'border-gray-400', iconClass: 'text-gray-500' };
}

export function DocumentDetailModal({ document: doc, categories, onClose, onUpdated }: DocumentDetailModalProps) {
  const { formatDate } = useFormatters();
  // CR-5858: switched the Activity Log from downloads-only to the full
  // /history/ audit feed so confirmations (and uploads, versions, accepts)
  // surface to both customer and supplier viewers.
  const [history, setHistory] = useState<DocumentHistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [importance, setImportance] = useState<string>(doc.importance || 'medium');
  const [category, setCategory] = useState(String(doc.category || ''));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc.id, doc.confirmation_version_token]);

  const fetchHistory = async () => {
    try {
      setHistoryLoading(true);
      const res = await documentsAPI.getDocumentHistory(doc.id);
      setHistory(res.data || []);
    } catch (err: unknown) {
      console.error('Failed to fetch audit history:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleImportanceChange = async (value: string) => {
    setImportance(value);
    try {
      setSaving(true);
      await documentsAPI.updateDocument(doc.id, { importance: value } as Partial<DocType>);
      onUpdated({ ...doc, importance: value as DocType['importance'] });
      toast.success('Importance updated');
    } catch (err: unknown) {
      console.error('Failed to update importance:', err);
      toast.error('Failed to update importance');
      setImportance(doc.importance || 'medium');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmationToggle = async (next: boolean) => {
    try {
      setSaving(true);
      await documentsAPI.updateDocument(doc.id, {
        requires_company_confirmation: next,
      } as Partial<DocType>);
      onUpdated({ ...doc, requires_company_confirmation: next });
      toast.success(
        next
          ? 'Read & Understood confirmation enabled'
          : 'Read & Understood confirmation disabled',
      );
    } catch (err: unknown) {
      console.error('Failed to update confirmation flag:', err);
      toast.error('Failed to update confirmation setting');
    } finally {
      setSaving(false);
    }
  };

  const handleCategoryChange = async (value: string) => {
    setCategory(value);
    const catId = value ? parseInt(value, 10) : null;
    try {
      setSaving(true);
      await documentsAPI.updateDocument(doc.id, { category: catId } as Partial<DocType>);
      const catName = categories.find((c) => c.id === catId)?.name || null;
      onUpdated({ ...doc, category: catId, category_name: catName || undefined });
      toast.success('Category updated');
    } catch (err: unknown) {
      console.error('Failed to update category:', err);
      toast.error('Failed to update category');
      setCategory(String(doc.category || ''));
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async () => {
    try {
      const res = await documentsAPI.downloadDocument(doc.id);
      if (res.data.file_url) window.open(res.data.file_url, '_blank');
      fetchHistory();
    } catch (err: unknown) {
      console.error('Failed to download:', err);
      toast.error('Failed to download document');
    }
  };

  const formatTimestamp = (ts: string) => {
    const date = new Date(ts);
    return {
      date: formatDate(ts),
      time: date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }),
    };
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-background rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            <h2 className="font-semibold text-lg truncate max-w-[500px]">{doc.title}</h2>
          </div>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Body — two columns */}
        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 divide-y lg:divide-y-0 lg:divide-x">
            {/* Left — Document Details */}
            <div className="p-6 space-y-5">
              {/* File Name */}
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">File Name</label>
                <p className="mt-1 font-medium">{doc.title}</p>
              </div>

              {/* Created Date */}
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Created Date</label>
                <p className="mt-1 flex items-center gap-1.5">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  {formatDate(doc.uploaded_at)}
                </p>
              </div>

              {/* Uploaded By */}
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Uploaded By</label>
                <p className="mt-1 flex items-center gap-1.5">
                  <User className="h-4 w-4 text-muted-foreground" />
                  {doc.uploaded_by_name || 'Unknown'}
                </p>
              </div>

              {/* File Size */}
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">File Size</label>
                <p className="mt-1">{doc.formatted_file_size || 'Unknown'}</p>
              </div>

              {/* Importance — Radio buttons like legacy */}
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Importance</label>
                <div className="flex gap-4 mt-2">
                  {IMPORTANCE_OPTIONS.map((opt) => (
                    <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="importance"
                        value={opt.value}
                        checked={importance === opt.value}
                        onChange={() => handleImportanceChange(opt.value)}
                        disabled={saving}
                        className="accent-primary"
                      />
                      <Badge className={`${IMPORTANCE_COLORS[opt.value]} text-xs`}>{opt.label}</Badge>
                    </label>
                  ))}
                </div>
              </div>

              {/* Category */}
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Category</label>
                <select
                  value={category}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  disabled={saving}
                  className="mt-1 w-full text-sm border rounded-md px-3 py-2 bg-background"
                >
                  <option value="">Uncategorized</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              {/* Download Button */}
              <Button onClick={handleDownload} className="w-full">
                <Download className="h-4 w-4 mr-2" />
                Download Document
              </Button>

              {/* CR-5858: Customer-side toggle to require/disable
                  Read & Understood confirmation for this document. */}
              <label className="flex items-start gap-3 cursor-pointer rounded-md border p-3 hover:bg-muted/30">
                <Checkbox
                  id="doc-detail-require-rnu"
                  checked={doc.requires_company_confirmation === true}
                  onCheckedChange={(v) => handleConfirmationToggle(v === true)}
                  disabled={saving}
                  className="mt-0.5"
                />
                <div>
                  <p className="text-sm font-medium flex items-center gap-1.5">
                    <ShieldCheck className="h-4 w-4 text-amber-600" />
                    Require Read &amp; Understood Confirmation
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    The supplier company must confirm before continuing.
                    A new version resets prior confirmations.
                  </p>
                </div>
              </label>

              {/* CR-5858: Read & Understood reporting panel */}
              <CompanyConfirmationPanel document={doc} />
            </div>

            {/* Right — Activity Log (full audit feed) */}
            <div className="p-6">
              <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Activity Log
              </h3>
              {historyLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : history.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No activity recorded yet.
                </p>
              ) : (
                <div className="relative">
                  <div className="absolute left-3 top-0 bottom-0 w-px bg-border" />
                  <div className="space-y-4">
                    {history.map((entry) => {
                      const ts = formatTimestamp(entry.change_date);
                      const visual = getEventVisual(entry);
                      return (
                        <div key={entry.id} className="relative pl-8">
                          <div className={`absolute left-1 w-5 h-5 rounded-full bg-background border-2 ${visual.borderClass} flex items-center justify-center`}>
                            <visual.Icon className={`h-2.5 w-2.5 ${visual.iconClass}`} />
                          </div>
                          <div className="bg-muted/30 rounded-lg p-3">
                            <p className="text-sm font-medium">{entry.field_changed}</p>
                            {entry.new_value && entry.event_type !== 'field_change' && (
                              <p className="text-xs text-muted-foreground mt-0.5 truncate">
                                {entry.new_value}
                              </p>
                            )}
                            <p className="text-xs text-muted-foreground mt-0.5">
                              By {entry.changed_by || 'System'}
                              {entry.changed_by_company ? ` · ${entry.changed_by_company}` : ''}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {ts.date} at {ts.time}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
